import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { BrowserWindow, BusinessLogger, Experiments, ViewerModel } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	BusinessLoggerSymbol,
	ExperimentsSymbol,
	MasterPageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	ViewerModelSym,
} from '@wix/thunderbolt-symbols'
import { links } from '@wix/thunderbolt-commons'
import type { ISessionManager } from 'feature-session-manager'
import { SessionManagerSymbol } from 'feature-session-manager'

import type { IMpaNavigation, MpaNavigationMasterPageConfig, MpaNavigationSiteConfig } from './types'
import { name } from './symbols'

export const MpaNavigation = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		ViewerModelSym,
		ExperimentsSymbol,
		BrowserWindowSymbol,
		BusinessLoggerSymbol,
		SessionManagerSymbol,
	],
	(
		siteConfig: MpaNavigationSiteConfig,
		masterPageConfig: MpaNavigationMasterPageConfig,
		viewerModel: ViewerModel,
		experiments: Experiments,
		browserWindow: BrowserWindow,
		businessLogger: BusinessLogger,
		sessionManager: ISessionManager
	): IMpaNavigation => {
		const isLoggedIn = () => {
			const useSiteMemberIdInsteadOfSmToken = !!experiments[
				'specs.thunderbolt.useSiteMemberIdInsteadOfSmToken2ndAttempt'
			]
			return !!(
				(useSiteMemberIdInsteadOfSmToken && sessionManager.getSiteMemberId()) ||
				sessionManager.getSmToken()
			)
		}

		return {
			isEligible: ({ anchorCompId, anchorDataId, skipHistory } = {}) => {
				if (siteConfig.forceMpaNavigation) {
					return true
				}

				const isEligibleToMpaNavigation =
					process.env.PACKAGE_NAME !== 'thunderbolt-ds' &&
					!!browserWindow &&
					!!viewerModel.mpaNavigationCompatible &&
					!masterPageConfig.hasPageTransition &&
					!isLoggedIn() &&
					!anchorCompId &&
					!anchorDataId &&
					!skipHistory

				if (isEligibleToMpaNavigation) {
					void businessLogger.logger.log(
						{
							src: 72,
							evid: 19,
							isEligible: isEligibleToMpaNavigation,
						},
						{ endpoint: 'bolt-performance' }
					)
				}

				return !!experiments['specs.thunderbolt.fullPageNavigationSpecificSites'] && isEligibleToMpaNavigation
			},
			navigate: (href: string) => {
				if (!browserWindow) {
					return false
				}

				const currentUrl = new URL(browserWindow.location.href)
				const navigableUrl = new URL(href)
				navigableUrl.protocol = currentUrl.protocol
				navigableUrl.href = links.appendInternalQueryParamsToHref(navigableUrl.href, currentUrl)

				if (navigableUrl.href === currentUrl.href) {
					return true
				}

				browserWindow.sessionStorage.setItem('isMpa', 'true')
				browserWindow.location.href = navigableUrl.href

				return true
			},
		}
	}
)
